/*
 * ═══════════════════════════════════════════════════════════════════
 * GeneralConfigDrawer — POS Store Configuration Drawer
 * ═══════════════════════════════════════════════════════════════════
 *
 * OVERVIEW:
 * A slide-in drawer that lets POS managers configure store-level
 * settings across 5 tabs: Cart, Logistics, Table, ODS & KDS, Refund.
 * All config is persisted to the backend (create/update) and mirrored
 * in localStorage under the key "appConfig" for instant app-wide access.
 *
 * ───────────────────────────────────────────────────────────────────
 * FUNCTIONS & THEIR ROLES:
 * ───────────────────────────────────────────────────────────────────
 *
 * [1] loadConfigFromAppConfig()
 *     - Runs on component mount (inside useState initializer).
 *     - Reads "appConfig" from localStorage (written by the dashboard
 *       on login or by storeAppConfig() after every save).
 *     - Maps the nested { enabledFeatures, settings } structure back
 *       into flat cfg field names (e.g. kotMode, tableOccupyMode).
 *     - Returns a partial cfg object; falls back to {} on any error.
 *
 * [2] defaultConfig  (constant, not a function)
 *     - Defines safe fallback values for every cfg field.
 *     - Used as the base layer in useState so no field is ever undefined.
 *
 * [3] en(bool)
 *     - Tiny helper: converts a boolean → "enable" | "disable".
 *     - Used throughout buildConfigurationDetails() to format values
 *       the way the backend API expects them.
 *
 * [4] buildConfigurationDetails(cfg)
 *     - Converts the flat cfg state object into the
 *       configurationDetails[] array shape required by the API.
 *     - Each entry follows: { key, flag, storeConfigSettings: { settings: {...} } }
 *     - Called inside handleSave() just before the API call.
 *
 * [5] storeAppConfig(configArray)  — defined inside the component
 *     - Receives the configurationDetails[] array from the
 *       create/update API response.
 *     - Transforms it into { enabledFeatures, settings, terminalSpecific, all }
 *       (same shape the dashboard query writes on login).
 *     - Writes the result to localStorage under "appConfig" so the
 *       rest of the app immediately reflects the saved config.
 *
 * [6] handleSave()  — defined inside the component
 *     - Triggered by the "Save Changes" button.
 *     - Flow:
 *         1. Builds the payload via buildConfigurationDetails(cfg).
 *         2. Calls getStoreConfig() to check if a config already exists.
 *         3. If storeConfigId exists  → calls updateStoreConfig().
 *            If not                  → calls createStoreConfig().
 *         4. On success, passes the API response to storeAppConfig()
 *            to refresh localStorage.
 *         5. Shows a success snackbar and closes the drawer.
 *         6. Shows an error snackbar if anything throws.
 *
 * [7] handleCancel()  — defined inside the component
 *     - Triggered by the "Cancel" button.
 *     - Resets cfg back to defaultConfig + current appConfig values
 *       (discards any unsaved UI changes) and closes the drawer.
 *
 * ───────────────────────────────────────────────────────────────────
 * LOCALSTORAGE KEY USED:
 *   "appConfig" — read on mount, written after every successful save.
 *
 * DATA FLOW SUMMARY:
 *   Mount  →  loadConfigFromAppConfig()  →  populate form fields
 *   Save   →  buildConfigurationDetails()  →  API (create/update)
 *          →  storeAppConfig(response)  →  update "appConfig"
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState } from "react";
import "./GeneralConfigDrawer.css";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import {
  createStoreConfig,
  getStoreConfig,
  updateStoreConfig,
} from "../../../../../../config/Services";

const TABS = ["Cart", "Logistics", "Table", "ODS & KDS", "Refund"];

const loadConfigFromAppConfig = () => {
  try {
    const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
    const settings = appConfig?.settings || {};
    const enabledFeatures = appConfig?.enabledFeatures || {};

    const getEnabled = (key, subKey) => settings?.[key]?.settings?.[subKey] === "enable";

    // KOT mode — whichever sub-key is "enable"
    const kotMode =
      ["ALWAYS", "ASK", "NEVER", "KDS"].find((v) => getEnabled("KOT_PRINT_MODE", v)) || "ALWAYS";

    // Bill print mode
    const printBillMode =
      ["ALWAYS", "ASK", "NEVER"].find((v) => getEnabled("BILL_PRINT_MODE", v)) || "ALWAYS";

    // Print bill on ODS status
    const printBillOnStatus =
      ["CREATED", "PREPARING", "PACKED"].find((v) => getEnabled("PRINT_BILL_ORDER_IN_ODS", v)) ||
      "CREATED";

    // Add tip section
    const addTipSection = getEnabled("ADD_TIP_SECTION", "ADD_TIP_SECTION");

    // Service charges
    const addServiceChargesDelivery =
      settings?.ADD_SERVICE_CHARGES_DELIVERY?.settings?.flag === "enable";
    const serviceChargeDeliveryPct =
      settings?.ADD_SERVICE_CHARGES_DELIVERY?.settings?.chargePercentage ?? "";
    const addServiceChargesPickup =
      settings?.ADD_SERVICE_CHARGES_PICKUP?.settings?.flag === "enable";
    const addServiceChargesDineIn =
      settings?.ADD_SERVICE_CHARGES_DINE_IN?.settings?.flag === "enable";

    // Display charges
    const displayDeliveryCharges =
      settings?.DISPLAY_DELIVERY_CHARGES_IN_BILL_DELIVERY?.settings?.flag === "enable";
    const displayPackingChargesDelivery =
      settings?.DISPLAY_PACKING_CHARGES_IN_BILL_DELIVERY?.settings?.flag === "enable";
    const displayPackingChargesPickup =
      settings?.DISPLAY_PACKING_CHARGES_IN_BILL_PICKUP?.settings?.flag === "enable";
    const displayPackingChargesDineIn =
      settings?.DISPLAY_PACKING_CHARGES_IN_BILL_DINE_IN?.settings?.flag === "enable";

    // Menu / price display
    const showPriceOnMenu =
      getEnabled("SHOW_PRICE_ON_MENU_ITEM_CARD", "PRICE_RANGE") ||
      getEnabled("SHOW_PRICE_ON_MENU_ITEM_CARD", "FIRST_VARIANT_PRICE");
    const priceDisplayMode = getEnabled("SHOW_PRICE_ON_MENU_ITEM_CARD", "PRICE_RANGE")
      ? "RANGE"
      : "FIRST_VARIANT";

    // Logistics — slotted order mode
    const slottedOrderMode = getEnabled(
      "PRINT_KOT_IMMEDIATELY_AFTER_ORDER_PLACED",
      "PRINT_KOT_IMMEDIATELY_AFTER_ORDER_PLACED"
    )
      ? "IMMEDIATE"
      : getEnabled("PRINT_KOT_BEFORE_DELIVERY_TIME", "PRINT_KOT_BEFORE_DELIVERY_TIME")
      ? "BEFORE_DELIVERY"
      : "IMMEDIATE";

    // Table — occupy mode
    const tableOccupyMode = getEnabled(
      "SHOW_CONFIRMATION_POPUP_TO_OCCUPY_TABLE",
      "SHOW_CONFIRMATION_POPUP_TO_OCCUPY_TABLE"
    )
      ? "CONFIRM_POPUP"
      : getEnabled("OCCUPY_TABLE_AFTER_ORDER_PLACED", "OCCUPY_TABLE_AFTER_ORDER_PLACED")
      ? "AUTO"
      : "CONFIRM_POPUP";

    const tableConfirmPopup = getEnabled("SHOW_CONFIRMATION_POPUP_TO_OCCUPY_TABLE", "SHOW_POPUP")
      ? "SHOW_POPUP"
      : "NO_POPUP";

    const tableReleaseMode = enabledFeatures?.AUTO_RELEASE_TABLE_AFTER_SUCCESSFUL_PAYMENT
      ? "PAYMENT"
      : enabledFeatures?.AUTO_RELEASE_TABLE_AFTER_BILL_PRINT
      ? "BILL_PRINT"
      : "";

    const tableReturnHome = enabledFeatures?.AUTO_RETURN_TO_HOME_AFTER_PAYMENT
      ? "AUTO"
      : enabledFeatures?.MANUAL_RETURN_TO_HOME
      ? "MANUAL"
      : "";

    // ODS & KDS
    const odsAcceptMode = getEnabled("AUTO_ACCEPT_ORDERS_IN_ODS", "AUTO_ACCEPT_ORDERS_IN_ODS")
      ? "AUTO"
      : "MANUAL";

    const kdsAcceptMode = getEnabled("AUTO_ACCEPT_ORDERS_IN_KDS", "AUTO_ACCEPT_ORDERS_IN_KDS")
      ? "AUTO"
      : getEnabled("MANUAL_ACCEPT_ORDERS_IN_KDS", "MANUAL_ACCEPT_ORDERS_IN_KDS")
      ? "MANUAL"
      : "";

    // Refund
    const refundNotifyMode = enabledFeatures?.REFUND_IS_INITIATED_DO_NOT_NOTIFY ? "NONE" : "NOTIFY";
    const refundSettings = settings?.REFUND_IS_INITIATED_NOTIFY_CUSTOMER?.settings || {};
    const refundNotifySms = refundSettings?.sms === "enable";
    const refundNotifyWhatsapp = refundSettings?.whatsapp === "enable";
    const refundNotifyEmail = refundSettings?.email === "enable";

    const refundDestination = enabledFeatures?.REFUND_TO_THE_SOURCE
      ? "SOURCE"
      : enabledFeatures?.REFUND_TO_THE_RMS_WALLET
      ? "RMS_WALLET"
      : enabledFeatures?.REFUND_AS_RMS_CREDIT_NOTE
      ? "CREDIT_NOTE"
      : "";

    return {
      kotMode,
      printBillMode,
      printBillOnStatus,
      addTipSection,
      addServiceChargesDelivery,
      serviceChargeDeliveryPct,
      addServiceChargesPickup,
      addServiceChargesDineIn,
      displayDeliveryCharges,
      displayPackingChargesDelivery,
      displayPackingChargesPickup,
      displayPackingChargesDineIn,
      showPriceOnMenu,
      priceDisplayMode,
      slottedOrderMode,
      tableOccupyMode,
      tableConfirmPopup,
      tableReleaseMode,
      tableReturnHome,
      odsAcceptMode,
      kdsAcceptMode,
      refundNotifyMode,
      refundNotifySms,
      refundNotifyWhatsapp,
      refundNotifyEmail,
      refundDestination,
    };
  } catch {
    return {};
  }
};

const defaultConfig = {
  /* ── Cart ── */
  kotMode: "ALWAYS", // ALWAYS | ASK | NEVER | KDS
  printBillMode: "ALWAYS", // ALWAYS | ASK | NEVER
  printBillOnStatus: "CREATED", // CREATED | PREPARING | PACKED
  addTipSection: false,
  addServiceChargesDelivery: false,
  serviceChargeDeliveryPct: "",
  addServiceChargesPickup: false,
  addServiceChargesDineIn: false,
  displayDeliveryCharges: false,
  displayPackingChargesDelivery: false,
  displayPackingChargesPickup: false,
  displayPackingChargesDineIn: false,
  showPriceOnMenu: true,
  priceDisplayMode: "RANGE", // FIRST_VARIANT | RANGE

  /* ── Logistics ── */
  slottedOrderMode: "IMMEDIATE", // IMMEDIATE | BEFORE_DELIVERY

  /* ── Table ── */
  tableOccupyMode: "CONFIRM_POPUP", // AUTO | CONFIRM_POPUP
  tableConfirmPopup: "NO_POPUP", // SHOW_POPUP | NO_POPUP
  tableReleaseMode: "", // PAYMENT | BILL_PRINT
  tableReturnHome: "", // AUTO | MANUAL

  /* ── ODS & KDS ── */
  odsAcceptMode: "AUTO", // MANUAL | AUTO
  kdsAcceptMode: "", // MANUAL | AUTO

  /* ── Refund ── */
  refundNotifyMode: "NOTIFY", // NONE | NOTIFY
  refundNotifySms: false,
  refundNotifyWhatsapp: false,
  refundNotifyEmail: false,
  refundDestination: "", // SOURCE | RMS_WALLET | CREDIT_NOTE
};

//  HELPERS

const en = (bool) => (bool ? "enable" : "disable");

// Wraps a settings object into the storeConfigSettings envelope
const wrap = (settings) => ({
  storeConfigSettings: { settings },
});

/* ─────────────────────────────────────────────────────────────────
   PAYLOAD BUILDER
   Each entry shape (single-level):
   {
     key: "ENUM_KEY",
     flag: true,
     storeConfigSettings: { settings: { KEY: "enable"|"disable", ... } }
   }

   Sub-typed shape (ADD_SERVICE_CHARGES, DISPLAY_PACKING_CHARGES,
   REFUND_IS_INITIATED_NOTIFY_CUSTOMER):
   {
     key: "ENUM_KEY",
     flag: true,
     storeConfigSettings: { settings: {  sub type: "enable"|"disable" } }
   }
───────────────────────────────────────────────────────────────── */
const buildConfigurationDetails = (cfg) => {
  const entries = [];

  const wrap = (settings) => ({
    storeConfigSettings: { settings },
  });

  const push = (key, settings) => {
    // Check if any value in settings equals "enable"
    const flag = Object.values(settings)?.some((v) => v === "enable");
    entries.push({ key, flag, ...wrap(settings) });
  };

  /* ── Cart ── */

  // KOT_PRINT_MODE
  push("KOT_PRINT_MODE", {
    ALWAYS: en(cfg?.kotMode === "ALWAYS"),
    ASK: en(cfg?.kotMode === "ASK"),
    NEVER: en(cfg?.kotMode === "NEVER"),
    KDS: en(cfg?.kotMode === "KDS"),
  });

  // BILL_PRINT_MODE
  push("BILL_PRINT_MODE", {
    ALWAYS: en(cfg?.printBillMode === "ALWAYS"),
    ASK: en(cfg?.printBillMode === "ASK"),
    NEVER: en(cfg?.printBillMode === "NEVER"),
  });

  // PRINT_BILL_ORDER_IN_ODS
  push("PRINT_BILL_ORDER_IN_ODS", {
    CREATED: en(cfg?.printBillOnStatus === "CREATED"),
    PREPARING: en(cfg?.printBillOnStatus === "PREPARING"),
    PACKED: en(cfg?.printBillOnStatus === "PACKED"),
  });

  // ADD_TIP_SECTION
  push("ADD_TIP_SECTION", {
    ADD_TIP_SECTION: en(cfg?.addTipSection),
  });

  // ADD_SERVICE_CHARGES — one entry per sub-type with type + charges fields
  push("ADD_SERVICE_CHARGES_DELIVERY", {
    flag: en(cfg?.addServiceChargesDelivery),
    chargePercentage: cfg?.addServiceChargesDelivery
      ? Number(cfg?.serviceChargeDeliveryPct) || 0
      : 0,
  });

  push("ADD_SERVICE_CHARGES_PICKUP", {
    flag: en(cfg?.addServiceChargesPickup),
    charges: 0,
  });

  push("ADD_SERVICE_CHARGES_DINE_IN", {
    flag: en(cfg?.addServiceChargesDineIn),
    charges: 0,
  });

  // DISPLAY_DELIVERY_CHARGES
  push("DISPLAY_DELIVERY_CHARGES_IN_BILL_DELIVERY", {
    flag: en(cfg?.displayDeliveryCharges),
  });

  // display packing charges in saperate saperate objects {delivery: '', Pickup: '', dine_in: ''}
  push("DISPLAY_PACKING_CHARGES_IN_BILL_DELIVERY", {
    flag: en(cfg?.displayPackingChargesDelivery),
  });

  push("DISPLAY_PACKING_CHARGES_IN_BILL_PICKUP", {
    flag: en(cfg?.displayPackingChargesPickup),
  });

  push("DISPLAY_PACKING_CHARGES_IN_BILL_DINE_IN", {
    flag: en(cfg?.displayPackingChargesDineIn),
  });

  // SHOW_PRICE_ON_MENU_ITEM_CARD
  push("SHOW_PRICE_ON_MENU_ITEM_CARD", {
    FIRST_VARIANT_PRICE: en(cfg?.showPriceOnMenu && cfg?.priceDisplayMode === "FIRST_VARIANT"),
    PRICE_RANGE: en(cfg?.showPriceOnMenu && cfg?.priceDisplayMode === "RANGE"),
  });

  /* ── Logistics ── */

  push("PRINT_KOT_IMMEDIATELY_AFTER_ORDER_PLACED", {
    PRINT_KOT_IMMEDIATELY_AFTER_ORDER_PLACED: en(cfg?.slottedOrderMode === "IMMEDIATE"),
  });

  push("PRINT_KOT_BEFORE_DELIVERY_TIME", {
    PRINT_KOT_BEFORE_DELIVERY_TIME: en(cfg?.slottedOrderMode === "BEFORE_DELIVERY"),
  });

  /* ── Table ── */

  push("OCCUPY_TABLE_AFTER_ORDER_PLACED", {
    OCCUPY_TABLE_AFTER_ORDER_PLACED: en(cfg?.tableOccupyMode === "AUTO"),
  });

  push("SHOW_CONFIRMATION_POPUP_TO_OCCUPY_TABLE", {
    SHOW_CONFIRMATION_POPUP_TO_OCCUPY_TABLE: en(cfg?.tableOccupyMode === "CONFIRM_POPUP"),
    SHOW_POPUP: en(
      cfg?.tableOccupyMode === "CONFIRM_POPUP" && cfg?.tableConfirmPopup === "SHOW_POPUP"
    ),
    NO_POPUP: en(cfg?.tableOccupyMode === "CONFIRM_POPUP" && cfg?.tableConfirmPopup === "NO_POPUP"),
  });

  push("AUTO_RELEASE_TABLE_AFTER_SUCCESSFUL_PAYMENT", {
    AUTO_RELEASE_TABLE_AFTER_SUCCESSFUL_PAYMENT: en(cfg?.tableReleaseMode === "PAYMENT"),
  });

  push("AUTO_RELEASE_TABLE_AFTER_BILL_PRINT", {
    AUTO_RELEASE_TABLE_AFTER_BILL_PRINT: en(cfg?.tableReleaseMode === "BILL_PRINT"),
  });

  push("AUTO_RETURN_TO_HOME_AFTER_PAYMENT", {
    AUTO_RETURN_TO_HOME_AFTER_PAYMENT: en(cfg?.tableReturnHome === "AUTO"),
  });

  push("MANUAL_RETURN_TO_HOME", {
    MANUAL_RETURN_TO_HOME: en(cfg?.tableReturnHome === "MANUAL"),
  });

  /* ── ODS & KDS ── */

  push("MANUAL_ACCEPT_ORDERS_IN_ODS", {
    MANUAL_ACCEPT_ORDERS_IN_ODS: en(cfg?.odsAcceptMode === "MANUAL"),
  });

  push("AUTO_ACCEPT_ORDERS_IN_ODS", {
    AUTO_ACCEPT_ORDERS_IN_ODS: en(cfg?.odsAcceptMode === "AUTO"),
  });

  push("MANUAL_ACCEPT_ORDERS_IN_KDS", {
    MANUAL_ACCEPT_ORDERS_IN_KDS: en(cfg?.kdsAcceptMode === "MANUAL"),
  });

  push("AUTO_ACCEPT_ORDERS_IN_KDS", {
    AUTO_ACCEPT_ORDERS_IN_KDS: en(cfg?.kdsAcceptMode === "AUTO"),
  });

  /* ── Refund ── */

  push("REFUND_IS_INITIATED_DO_NOT_NOTIFY", {
    REFUND_IS_INITIATED_DO_NOT_NOTIFY: en(cfg?.refundNotifyMode === "NONE"),
  });

  // REFUND_IS_INITIATED_NOTIFY_CUSTOMER — one entry per channel sub-type

  //  SINGLE MERGED ENTRY
  const isNotify = cfg?.refundNotifyMode === "NOTIFY";
  const notifySettings = {
    // REFUND_IS_INITIATED_NOTIFY_CUSTOMER: en(isNotify),
    sms: en(isNotify && cfg?.refundNotifySms),
    whatsapp: en(isNotify && cfg?.refundNotifyWhatsapp),
    email: en(isNotify && cfg?.refundNotifyEmail),
  };
  const notifyFlag = Object.values(notifySettings).some((v) => v === "enable");
  entries.push({
    key: "REFUND_IS_INITIATED_NOTIFY_CUSTOMER",
    flag: notifyFlag,
    storeConfigSettings: { settings: notifySettings },
  });

  push("REFUND_TO_THE_SOURCE", {
    REFUND_TO_THE_SOURCE: en(cfg?.refundDestination === "SOURCE"),
  });

  push("REFUND_TO_THE_RMS_WALLET", {
    REFUND_TO_THE_RMS_WALLET: en(cfg?.refundDestination === "RMS_WALLET"),
  });

  push("REFUND_AS_RMS_CREDIT_NOTE", {
    REFUND_AS_RMS_CREDIT_NOTE: en(cfg?.refundDestination === "CREDIT_NOTE"),
  });

  return entries;
};

/* ─── Small reusable primitives ─── */
const RadioGroup = ({ name, value, onChange, options }) => (
  <div className="gcd-radio-group">
    {options.map(({ val, label }) => (
      <label key={val} className="gcd-radio-label">
        <input
          type="radio"
          name={name}
          value={val}
          checked={value === val}
          onChange={() => onChange(val)}
        />
        {label}
      </label>
    ))}
  </div>
);

const CheckItem = ({ checked, onChange, label }) => (
  <label className="gcd-checkbox-label">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const Divider = () => <hr className="gcd-divider" />;

/* ══════════════════════════════════════════════════════════════ */
const GeneralConfigDrawer = ({ open, onClose }) => {
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const userDetails = JSON.parse(localStorage.getItem("retailUserDetails"));
  const userName = localStorage.getItem("user_name");
  const retailType = (localStorage.getItem("retailType") || "").toUpperCase();
  const isGrocery = retailType === "GROCERY";
  const showSnackbar = useSnackbar();
  const [activeTab, setActiveTab] = useState("Cart");

  const [cfg, setCfg] = useState(() => ({
    ...defaultConfig,
    ...loadConfigFromAppConfig(), // appConfig takes priority if present
  }));

  const set = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));

  const storeAppConfig = (configArray) => {
    if (!configArray || !Array.isArray(configArray) || configArray.length === 0) return;

    const config = {
      enabledFeatures: {},
      settings: {},
      terminalSpecific: {},
      all: {},
    };

    configArray.forEach((item) => {
      config.all[item.key] = item;

      if (item.flag) {
        config.enabledFeatures[item.key] = true;
      }

      if (item.storeConfigSettings) {
        config.settings[item.key] = item.storeConfigSettings;
      }

      if (item.terminalIds && item.terminalIds.length > 0) {
        config.terminalSpecific[item.key] = item.terminalIds;
      }
    });

    localStorage.setItem("appConfig", JSON.stringify(config));
  };

  const handleSave = () => {
    const configurationDetails = buildConfigurationDetails(cfg);

    getStoreConfig(locId)
      .then((res) => {
        if (res.data?.statusCode === 200) {
          if (res.data.storeConfig?.storeConfigId) {
            const updatePayload = {
              storeConfigId: res.data.storeConfig.storeConfigId,
              updatedBy: userDetails?.uidx ?? "",
              updatedByName: userName,
              configurationDetails,
            };
            updateStoreConfig(updatePayload).then((updateRes) => {
              const updatedConfigArray = updateRes?.data?.data?.storeConfig?.configurationDetails;

              storeAppConfig(updatedConfigArray);
            });
          } else {
            const createPayload = {
              orgId: orgId,
              locId: locId,
              createdBy: userDetails?.uidx ?? "",
              createdByName: userName,
              updatedBy: userDetails?.uidx ?? "",
              updatedByName: userName,
              configurationDetails,
            };
            createStoreConfig(createPayload).then((createRes) => {
              const createdConfigArray = createRes?.data?.data?.storeConfig?.configurationDetails;
              storeAppConfig(createdConfigArray);
            });
          }
        }
      })
      .then(() => {
        // localStorage.setItem("generalConfig", JSON.stringify(cfg));
        showSnackbar("Configuration saved successfully", "success");
        onClose();
      })
      .catch((err) => {
        showSnackbar("Failed to configure", "error");
      });
  };

  const handleCancel = () => {
    setCfg({ ...defaultConfig, ...loadConfigFromAppConfig() });
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div className="gcd-overlay" onClick={onClose} />
      <div className="gcd-drawer">
        {/* ── Header ── */}
        <div className="gcd-header">
          <div className="gcd-header-top">
            <div>
              <p className="gcd-title">Configuration</p>
              <p className="gcd-subtitle">Configure your POS to your linking</p>
            </div>
            <button className="gcd-close-btn" onClick={onClose}>
              <XMarkIcon style={{ width: 22, height: 22 }} />
            </button>
          </div>
          <div className="gcd-tabs">
            {(isGrocery ? TABS?.filter((tab) => tab !== "Table") : TABS)?.map((tab) => (
              <button
                key={tab}
                className={`gcd-tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="gcd-body">
          {/* ════════════ CART ════════════ */}
          {activeTab === "Cart" && (
            <>
              {!isGrocery && (
                <>
                  <div className="gcd-section">
                    <p className="gcd-section-title">KOT Configuration</p>
                    <RadioGroup
                      name="kotMode"
                      value={cfg?.kotMode}
                      onChange={(v) => set("kotMode", v)}
                      options={[
                        { val: "ALWAYS", label: "Automatically print KOT" },
                        { val: "ASK", label: "Ask before printing KOT" },
                        { val: "NEVER", label: "Do not print KOT" },
                        { val: "KDS", label: "Show orders in KDS" },
                      ]}
                    />
                  </div>

                  <Divider />
                </>
              )}

              <div className="gcd-section">
                <p className="gcd-section-title">Print Bill Configuration</p>
                <RadioGroup
                  name="printBillMode"
                  value={cfg?.printBillMode}
                  onChange={(v) => set("printBillMode", v)}
                  options={[
                    { val: "ALWAYS", label: "Automatically print the bill" },
                    { val: "ASK", label: "Ask before printing the bill" },
                    { val: "NEVER", label: "Do not print the bill" },
                  ]}
                />
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Print Bill when the order is (ODS Status)</p>
                <RadioGroup
                  name="printBillOnStatus"
                  value={cfg?.printBillOnStatus}
                  onChange={(v) => set("printBillOnStatus", v)}
                  options={[
                    { val: "CREATED", label: "Created" },
                    { val: "PREPARING", label: "Preparing" },
                    { val: "PACKED", label: "Packed" },
                  ]}
                />
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Bill Content Configuration</p>
                <div className="gcd-checkbox-group">
                  <CheckItem
                    checked={cfg?.addTipSection}
                    onChange={(v) => set("addTipSection", v)}
                    label="Add tip section"
                  />

                  <CheckItem
                    checked={cfg?.addServiceChargesDelivery}
                    onChange={(v) => set("addServiceChargesDelivery", v)}
                    label="Add service charges (Delivery)"
                  />
                  {cfg?.addServiceChargesDelivery && (
                    <div className="gcd-charge-input-wrapper">
                      <span className="gcd-charge-input-label">Insert Charge Percentage</span>
                      <input
                        className="gcd-charge-input"
                        type="number"
                        placeholder="e.g. 5%"
                        value={cfg?.serviceChargeDeliveryPct}
                        onChange={(e) => set("serviceChargeDeliveryPct", e.target.value)}
                      />
                    </div>
                  )}

                  <CheckItem
                    checked={cfg?.addServiceChargesPickup}
                    onChange={(v) => set("addServiceChargesPickup", v)}
                    label="Add service charges (Pickup)"
                  />
                  <CheckItem
                    checked={cfg?.addServiceChargesDineIn}
                    onChange={(v) => set("addServiceChargesDineIn", v)}
                    label="Add service charges (Dine-in)"
                  />
                  <CheckItem
                    checked={cfg?.displayDeliveryCharges}
                    onChange={(v) => set("displayDeliveryCharges", v)}
                    label="Display delivery charges in bill (Delivery)"
                  />
                  <CheckItem
                    checked={cfg?.displayPackingChargesDelivery}
                    onChange={(v) => set("displayPackingChargesDelivery", v)}
                    label="Display packing charges in bill (Delivery)"
                  />
                  <CheckItem
                    checked={cfg?.displayPackingChargesPickup}
                    onChange={(v) => set("displayPackingChargesPickup", v)}
                    label="Display packing charges in bill (Pickup)"
                  />
                  <CheckItem
                    checked={cfg?.displayPackingChargesDineIn}
                    onChange={(v) => set("displayPackingChargesDineIn", v)}
                    label="Display packing charges in bill (Dine-in)"
                  />
                </div>
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Menu Configuration</p>
                <div className="gcd-checkbox-group">
                  <CheckItem
                    checked={cfg?.showPriceOnMenu}
                    onChange={(v) => set("showPriceOnMenu", v)}
                    label="Show price on menu item card"
                  />
                  {cfg?.showPriceOnMenu && (
                    <div className="gcd-sub-section">
                      <p className="gcd-sub-section-title">
                        On selecting show price on menu item card
                      </p>
                      <RadioGroup
                        name="priceDisplayMode"
                        value={cfg?.priceDisplayMode}
                        onChange={(v) => set("priceDisplayMode", v)}
                        options={[
                          { val: "FIRST_VARIANT", label: "Show first variant price" },
                          { val: "RANGE", label: "Show price range of variants (e.g. ₹100-₹500)" },
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ════════════ LOGISTICS ════════════ */}
          {activeTab === "Logistics" && (
            <div className="gcd-section">
              <p className="gcd-section-title">Slotted Order Configuration</p>
              <RadioGroup
                name="slottedOrderMode"
                value={cfg?.slottedOrderMode}
                onChange={(v) => set("slottedOrderMode", v)}
                options={[
                  { val: "IMMEDIATE", label: "Print KOT immediately after the order is placed" },
                  {
                    val: "BEFORE_DELIVERY",
                    label: "Print KOT a specified number of minutes before the delivery time",
                  },
                ]}
              />
            </div>
          )}

          {/* ════════════ TABLE ════════════ */}
          {activeTab === "Table" && (
            <>
              <div className="gcd-section">
                <p className="gcd-section-title">Table Management Configuration</p>
                <RadioGroup
                  name="tableOccupyMode"
                  value={cfg?.tableOccupyMode}
                  onChange={(v) => set("tableOccupyMode", v)}
                  options={[
                    { val: "AUTO", label: "Occupy table after placing order" },
                    {
                      val: "CONFIRM_POPUP",
                      label: "On table click, show a confirmation pop-up to occupy the table",
                    },
                  ]}
                />
                {cfg?.tableOccupyMode === "CONFIRM_POPUP" && (
                  <div className="gcd-sub-section" style={{ marginTop: 12 }}>
                    <p className="gcd-sub-section-title">
                      On selecting table show confirmation pop-up to occupy table
                    </p>
                    <RadioGroup
                      name="tableConfirmPopup"
                      value={cfg?.tableConfirmPopup}
                      onChange={(v) => set("tableConfirmPopup", v)}
                      options={[
                        {
                          val: "SHOW_POPUP",
                          label:
                            "Yes Show pop-up (this process will take more clicks to occupy tables)",
                        },
                        {
                          val: "NO_POPUP",
                          label:
                            "No, Do not show pop up (this process takes less clicks to occupy tables)",
                        },
                      ]}
                    />
                  </div>
                )}
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Table Release Configuration</p>
                <RadioGroup
                  name="tableReleaseMode"
                  value={cfg?.tableReleaseMode}
                  onChange={(v) => set("tableReleaseMode", v)}
                  options={[
                    { val: "PAYMENT", label: "Auto-Release table after successful payment" },
                    { val: "BILL_PRINT", label: "Auto-Release table after bill print" },
                  ]}
                />
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Return To Home After Table Release</p>
                <RadioGroup
                  name="tableReturnHome"
                  value={cfg?.tableReturnHome}
                  onChange={(v) => set("tableReturnHome", v)}
                  options={[
                    { val: "AUTO", label: "Auto-return to home after successful payment" },
                    { val: "MANUAL", label: "Manual return to home" },
                  ]}
                />
              </div>
            </>
          )}

          {/* ════════════ ODS & KDS ════════════ */}
          {activeTab === "ODS & KDS" && (
            <>
              <div className="gcd-section">
                <p className="gcd-section-title">Accepting orders in ODS</p>
                <RadioGroup
                  name="odsAcceptMode"
                  value={cfg?.odsAcceptMode}
                  onChange={(v) => set("odsAcceptMode", v)}
                  options={[
                    { val: "MANUAL", label: "Manually accept orders" },
                    { val: "AUTO", label: "Automatically accept orders" },
                  ]}
                />
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Accepting orders in KDS</p>
                <RadioGroup
                  name="kdsAcceptMode"
                  value={cfg?.kdsAcceptMode}
                  onChange={(v) => set("kdsAcceptMode", v)}
                  options={[
                    { val: "MANUAL", label: "Manually accept orders" },
                    { val: "AUTO", label: "Automatically accept orders" },
                  ]}
                />
              </div>
            </>
          )}

          {/* ════════════ REFUND ════════════ */}
          {activeTab === "Refund" && (
            <>
              <div className="gcd-section">
                <p className="gcd-section-title">When Refund Is Initiated</p>
                <RadioGroup
                  name="refundNotifyMode"
                  value={cfg?.refundNotifyMode}
                  onChange={(v) => set("refundNotifyMode", v)}
                  options={[
                    { val: "NONE", label: "Do not Notify" },
                    { val: "NOTIFY", label: "Notify customer" },
                  ]}
                />
                {cfg?.refundNotifyMode === "NOTIFY" && (
                  <div className="gcd-sub-section" style={{ marginTop: 12 }}>
                    <p className="gcd-sub-section-title">Notify via</p>
                    <div className="gcd-checkbox-group">
                      <CheckItem
                        checked={cfg?.refundNotifySms}
                        onChange={(v) => set("refundNotifySms", v)}
                        label="SMS"
                      />
                      <CheckItem
                        checked={cfg?.refundNotifyWhatsapp}
                        onChange={(v) => set("refundNotifyWhatsapp", v)}
                        label="Whatsapp"
                      />
                      <CheckItem
                        checked={cfg?.refundNotifyEmail}
                        onChange={(v) => set("refundNotifyEmail", v)}
                        label="Email"
                      />
                    </div>
                  </div>
                )}
              </div>

              <Divider />

              <div className="gcd-section">
                <p className="gcd-section-title">Refund Destination</p>
                <RadioGroup
                  name="refundDestination"
                  value={cfg?.refundDestination}
                  onChange={(v) => set("refundDestination", v)}
                  options={[
                    { val: "SOURCE", label: "Refund to the Source" },
                    { val: "RMS_WALLET", label: "Refund to the RMS wallet" },
                    { val: "CREDIT_NOTE", label: "Refund as RMS credit note" },
                  ]}
                />
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="gcd-footer">
          <button className="gcd-cancel-btn" onClick={handleCancel}>
            <XMarkIcon style={{ width: 16, height: 16 }} />
            Cancel
          </button>
          <button className="gcd-save-btn" onClick={handleSave}>
            Save Changes
            <CheckCircleIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>
    </>
  );
};

export default GeneralConfigDrawer;
